import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  useMediaQuery,
  useTheme,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Collapse,
  IconButton,
  Slider,
  TablePagination,
} from "@mui/material";
import { FilterList, Clear } from "@mui/icons-material";

// Static exchange rate (INR to CNY) - Replace with API call in production
const EXCHANGE_RATE_INR_TO_CNY = 0.083;

const OrderHistory = ({ orders }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedLanguage = localStorage.getItem("selectedLanguage") || "en";

  // Filter states
  const [filterOpen, setFilterOpen] = useState(false);
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [amountRange, setAmountRange] = useState([0, 10000]);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filter options
  const paymentMethods = ["cod", "card"];
  const paymentStatuses = ["Pending", "Paid", "Failed"];
  const orderStatuses = [
    "Confirmed",
    "pending",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
  ];

  // Filter logic
  const filteredOrders = orders.filter((order) => {
    const orderDate = new Date(order.createdAt);
    const amountInSelectedCurrency =
      selectedLanguage === "zh-TW"
        ? order.totalAmount * EXCHANGE_RATE_INR_TO_CNY
        : order.totalAmount;

    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;

    return (
      (orderIdFilter === "" ||
        order._id.toLowerCase().includes(orderIdFilter.toLowerCase())) &&
      amountInSelectedCurrency >= amountRange[0] &&
      amountInSelectedCurrency <= amountRange[1] &&
      (paymentMethodFilter === "" ||
        order.paymentMethod === paymentMethodFilter) &&
      (paymentStatusFilter.length === 0 ||
        paymentStatusFilter.includes(order.paymentStatus)) &&
      (orderStatusFilter.length === 0 ||
        orderStatusFilter.includes(order.orderStatus)) &&
      (!startDate ||
        !endDate ||
        (orderDate >= startDate && orderDate <= endDate))
    );
  });

  // Pagination logic
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handlers
  const handleClearFilters = () => {
    setOrderIdFilter("");
    setAmountRange([0, 10000]);
    setPaymentMethodFilter("");
    setPaymentStatusFilter([]);
    setOrderStatusFilter([]);
    setDateRange({ start: "", end: "" });
    setPage(0); // Reset to first page
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const getStatusColor = (status) => {
    const statusColors = {
      confirmed: "secondary",
      pending: "warning",
      processing: "info",
      shipped: "primary",
      delivered: "success",
      cancelled: "error",
      paid: "success",
      failed: "error",
    };
    return statusColors[status.toLowerCase()] || "default";
  };

  const getPriceDetails = (amount) => {
    const isChinese = selectedLanguage === "zh-TW";
    const currencySymbol = isChinese ? "¥" : "₹";
    const convertedAmount = isChinese
      ? (amount * EXCHANGE_RATE_INR_TO_CNY).toFixed(2)
      : amount.toFixed(2);
    return `${currencySymbol}${convertedAmount}`;
  };

  const renderShippingAddress = (address) => (
    <Box sx={{ mt: 1, color: "#555" }}>
      <Typography variant="body2">
        <strong>Name:</strong> {address.name}
      </Typography>
      <Typography variant="body2">
        <strong>Email:</strong> {address.email}
      </Typography>
      <Typography variant="body2">
        <strong>Address:</strong> {address.address}, {address.city},{" "}
        {address.state}, {address.zipCode}
      </Typography>
      <Typography variant="body2">
        <strong>Phone:</strong> {address.phone}
      </Typography>
    </Box>
  );

  return (
    <Box
      p={2}
      sx={{
        // background: "linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)",
        minHeight: "100vh",
        borderRadius: 2,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#0277bd",
            fontWeight: "bold",
            animation: "fadeIn 1s ease-in",
          }}
        >
          Order History ({filteredOrders.length} orders)
        </Typography>
        <IconButton
          onClick={() => setFilterOpen(!filterOpen)}
          sx={{ color: "#0288d1", "&:hover": { color: "#01579b" } }}
        >
          <FilterList />
        </IconButton>
      </Box>

      {/* Filter Section */}
      <Collapse in={filterOpen}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            background: "rgba(255, 255, 255, 0.9)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            animation: "slideIn 0.5s ease-out",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Order ID"
                value={orderIdFilter}
                onChange={(e) => setOrderIdFilter(e.target.value)}
                size="small"
                sx={{
                  input: { color: "#0277bd" },
                  label: { color: "#0288d1" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "#0288d1" }}>
                  Payment Method
                </InputLabel>
                <Select
                  value={paymentMethodFilter}
                  label="Payment Method"
                  onChange={(e) => setPaymentMethodFilter(e.target.value)}
                  sx={{ color: "#0277bd" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "#0288d1" }}>
                  Payment Status
                </InputLabel>
                <Select
                  multiple
                  value={paymentStatusFilter}
                  label="Payment Status"
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  renderValue={(selected) => selected.join(", ")}
                  sx={{ color: "#0277bd" }}
                >
                  {paymentStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "#0288d1" }}>Order Status</InputLabel>
                <Select
                  multiple
                  value={orderStatusFilter}
                  label="Order Status"
                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                  renderValue={(selected) => selected.join(", ")}
                  sx={{ color: "#0277bd" }}
                >
                  {orderStatuses.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography gutterBottom sx={{ color: "#0288d1" }}>
                Amount Range ({selectedLanguage === "zh-TW" ? "¥" : "₹"})
              </Typography>
              <Slider
                value={amountRange}
                onChange={(e, newValue) => setAmountRange(newValue)}
                valueLabelDisplay="auto"
                min={0}
                max={10000}
                step={100}
                sx={{ color: "#0288d1" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box display="flex" gap={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, start: e.target.value })
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    input: { color: "#0277bd" },
                    label: { color: "#0288d1" },
                  }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange({ ...dateRange, end: e.target.value })
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    input: { color: "#0277bd" },
                    label: { color: "#0288d1" },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  sx={{
                    borderColor: "#0288d1",
                    color: "#0288d1",
                    "&:hover": { borderColor: "#01579b", color: "#01579b" },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Orders Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          background: "#fff",
          overflowX: { xs: "auto", md: "hidden" },
        }}
      >
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                background: "linear-gradient(90deg, #0288d1 0%, #0277bd 100%)",
              }}
            >
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Order ID
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Total Amount
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Payment Method
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Payment Status
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Order Status
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Shipping Address
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                Ordered At
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.length > 0 ? (
              paginatedOrders.map((order, index) => (
                <TableRow
                  key={order._id}
                  sx={{
                    "&:hover": { backgroundColor: "#e1f5fe" },
                    animation: `rowFadeIn 0.5s ease-in ${index * 0.1}s`,
                    backgroundColor: index % 2 === 0 ? "#f5f5f5" : "#fff",
                  }}
                >
                  <TableCell sx={{ color: "#0277bd" }}>
                    {order._id.slice(-6)}
                  </TableCell>
                  <TableCell sx={{ color: "#0277bd" }}>
                    {getPriceDetails(order.totalAmount)}
                  </TableCell>
                  <TableCell sx={{ color: "#0277bd" }}>
                    {order.paymentMethod.toUpperCase()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.paymentStatus}
                      color={getStatusColor(order.paymentStatus)}
                      size="small"
                      sx={{
                        fontWeight: "bold",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.1)" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.orderStatus}
                      color={getStatusColor(order.orderStatus)}
                      size="small"
                      sx={{
                        fontWeight: "bold",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.1)" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {order.shippingAddress
                      ? renderShippingAddress(order.shippingAddress)
                      : "N/A"}
                  </TableCell>
                  <TableCell sx={{ color: "#0277bd" }}>
                    {new Date(order.createdAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography sx={{ color: "#0288d1", py: 2 }}>
                    No orders match the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          ...(isMobile
            ? {
                position: "sticky",
                bottom: 0,
                background: "rgba(255, 255, 255, 0.95)",
                boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
                zIndex: 1,
                borderRadius: "0 0 8px 8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
              }
            : {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                background: "rgba(255, 255, 255, 0.95)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                mt: 1,
              }),
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: "#0277bd",
            "& .MuiTablePagination-selectLabel": {
              color: "#0277bd",
              fontWeight: "bold",
              margin: 0,
            },
            "& .MuiTablePagination-displayedRows": {
              color: "#0277bd",
              fontWeight: "bold",
              margin: 0,
            },
            "& .MuiTablePagination-select": {
              color: "#0277bd",
              fontWeight: "bold",
              padding: "4px 8px",
              borderRadius: "4px",
              backgroundColor: "#e0f7fa",
            },
            "& .MuiTablePagination-menuItem": {
              color: "#0277bd",
            },
            "& .MuiIconButton-root": {
              color: "#0288d1",
              padding: "4px",
              "&:hover": { color: "#01579b", backgroundColor: "#b2ebf2" },
            },
            "& .MuiTablePagination-toolbar": {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              minHeight: "unset", // Remove default min-height for tighter control
              padding: 0,
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              gap: "4px", // Equal spacing between buttons
            },
          }}
        />
      </Box>

      {/* CSS Keyframes for Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { transform: translateY(-20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes rowFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Box>
  );
};

export default OrderHistory;
